import { IconAtSign, IconUsers } from '../../../common/icons';
import { Gate } from '../../../global/contexts/auth/auth';
import {
    EMAIL_MAX,
    NAME_MAX,
    PASSWORD_MAX,
    PASSWORD_MIN,
    ROLE_OPTIONS,
    STATUS_OPTIONS,
} from '../utils/form';

import '../../clients/styles/form.css';

// The user form body, shared by create and edit.
//
// It reuses the clients form stylesheet on purpose: it is the same form
// language from design/referencias/form.png, and a second copy of those rules
// under another class name is how two forms start looking different by
// accident. The day a third one appears, that file moves to common/.

export default function UserFields({
    values,
    errors = {},
    onChange,
    disabled,
    withPassword,
    roleDisabled,
    visible,
}) {
    const set = (field) => (e) => onChange(field, e.target.value);
    // `visible` undefined means show everything, which is what the edit form
    // does: editing is one page, never a walk through steps.
    const show = (field) => !visible || visible.includes(field);
    const role = ROLE_OPTIONS.find(option => option.value === values.role);

    return (
        <div className="client-form__grid">
            {show('name') && (
                <label className="client-form__field">
                    <span className="client-form__label">
                        Nombre <span className="client-form__required">*</span>
                    </span>
                    <span className="client-form__control">
                        <input
                            className={`client-form__input ${errors.name ? 'client-form__input--invalid' : ''}`}
                            type="text"
                            value={values.name}
                            onChange={set('name')}
                            maxLength={NAME_MAX}
                            placeholder="Nombre y apellido"
                            disabled={disabled}
                            required
                        />
                        <span className="client-form__icon" aria-hidden="true"><IconUsers /></span>
                    </span>
                    {errors.name && <span className="client-form__error">{errors.name}</span>}
                </label>
            )}

            {show('email') && (
                <label className="client-form__field">
                    <span className="client-form__label">
                        Correo <span className="client-form__required">*</span>
                    </span>
                    <span className="client-form__control">
                        <input
                            className={`client-form__input ${errors.email ? 'client-form__input--invalid' : ''}`}
                            type="email"
                            value={values.email}
                            onChange={set('email')}
                            maxLength={EMAIL_MAX}
                            placeholder="persona@trianametria.com"
                            autoComplete="off"
                            disabled={disabled}
                            required
                        />
                        <span className="client-form__icon" aria-hidden="true"><IconAtSign /></span>
                    </span>
                    {errors.email && <span className="client-form__error">{errors.email}</span>}
                </label>
            )}

            {/* Only when creating. No route in this API sets somebody else's
                password: /auth/change-password is each person's own. */}
            {withPassword && show('password') && (
                <label className="client-form__field client-form__field--wide">
                    <span className="client-form__label">
                        Contraseña <span className="client-form__required">*</span>
                    </span>
                    <span className="client-form__control">
                        <input
                            className={`client-form__input ${errors.password ? 'client-form__input--invalid' : ''}`}
                            type="password"
                            value={values.password}
                            onChange={set('password')}
                            minLength={PASSWORD_MIN}
                            maxLength={PASSWORD_MAX}
                            placeholder={`Al menos ${PASSWORD_MIN} caracteres`}
                            autoComplete="new-password"
                            disabled={disabled}
                            required
                        />
                    </span>
                    {errors.password
                        ? <span className="client-form__error">{errors.password}</span>
                        : (
                            <span className="client-form__note">
                                Se la das vos esta vez. Después la cambia cada persona desde
                                su cuenta: no hay forma de cambiar la contraseña de otro.
                            </span>
                        )}
                </label>
            )}

            {/* Choosing a role is choosing what somebody can do, so it is
                behind `users:admin` — the permission the backend puts on
                PATCH /admin/users/{id}/role. */}
            <Gate domain="users:admin">
                {show('role') && (
                <label className="client-form__field">
                    <span className="client-form__label">Rol</span>
                    <span className="client-form__control">
                        <select
                            className="client-form__input client-form__input--select"
                            value={values.role}
                            onChange={set('role')}
                            disabled={disabled || roleDisabled}
                        >
                            {ROLE_OPTIONS.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </span>
                    {errors.role
                        ? <span className="client-form__error">{errors.role}</span>
                        : role && <span className="client-form__note">{role.hint}</span>}
                </label>
                )}
            </Gate>

            {show('status') && (
                <label className="client-form__field">
                    <span className="client-form__label">Estado</span>
                    <span className="client-form__control">
                        <select
                            className="client-form__input client-form__input--select"
                            value={values.status}
                            onChange={set('status')}
                            disabled={disabled}
                        >
                            {STATUS_OPTIONS.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </span>
                    {errors.status && <span className="client-form__error">{errors.status}</span>}
                </label>
            )}
        </div>
    );
}
